import React, { useLayoutEffect, useRef,useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact, {
	HighchartsReactRefObject,
} from 'highcharts-react-official';

interface DataPoint {
	x: number; // timestamp
	y: number;
}

interface AreaChartProps {
	title?: string;
	chargerPerformanceData?: IChargerSessionPerformanceData[];
}

const ChargingSessionAreaChart: React.FC<AreaChartProps> = ({
	title,
	chargerPerformanceData,
}) => {
	const chartRef = React.useRef<HighchartsReactRefObject>(null);
	// const [chartSize, setChartSize] = React.useState<{
	//  width: number | null;
	//  height: number | null;
	// }>({ width: null, height: 200 });
	const chartWrapRef = useRef<HTMLDivElement>(null);
	const chartSizeRef = useRef<{ width: number; height: number }>({
		width: 0,
		height: 200,
	});

	const perfomanceData = chargerPerformanceData || [];
	const len = perfomanceData.length;

	// Convert hourly data to DataPoint format
	const defaultEnergyData: DataPoint[] = perfomanceData.map((item, index) => ({
		x: index,
		y: item.energyDispensed,
	}));

	const defaultChargingTimeData: DataPoint[] = perfomanceData.map(
		(item, index) => ({
			x: index,
			y: item.chargingTime,
		}),
	);

	const finalEnergyData = defaultEnergyData;
	const finalChargingTimeData = defaultChargingTimeData;

	const options: Highcharts.Options =  useMemo(() =>
		({
		chart: {
			type: 'spline',
			backgroundColor: 'transparent',
			// height: 300,
		},

		title: {
			text: title,
			align: 'left',
			style: {
				fontSize: '15px',
				color: '#666666',
				fontWeight: 'normal',
			},
		},

		credits: { enabled: false },
		exporting: { enabled: false },

		xAxis: {
			type: 'category',
			categories: perfomanceData.map((item) => item.date),
			labels: {
				style: {
					color: '#999',
					fontSize: '11px',
				},
				step: Math.ceil(len / 10),
			},
			lineColor: '#e5e5e5',
			tickColor: '#e5e5e5',
		},

		yAxis: [
			{
				title: {
					text: 'Energy (kWh)',
					style: {
						color: '#00bcd4',
						fontSize: '12px',
						fontWeight: '500',
					},
				},
				labels: {
					style: {
						color: '#00bcd4',
						fontSize: '12px',
					},
				},
				gridLineColor: '#f5f5f5',
			},
			{
				title: {
					text: 'Charging Time (mins)',
					style: {
						color: '#5e7a9b',
						fontSize: '12px',
						fontWeight: '500',
					},
				},
				labels: {
					style: {
						color: '#5e7a9b',
						fontSize: '12px',
					},
				},
				opposite: true,
				gridLineWidth: 0,
			},
		],

		legend: {
			enabled: false,
		},

		plotOptions: {
			spline: {
				lineWidth: 3,
				marker: {
					enabled: false,
				},
				states: {
					hover: {
						lineWidthPlus: 0,
					},
				},
			},
			series: {
				softThreshold: true,
				lineWidth: 3,
			},
		},

		tooltip: {
			shared: true,
			useHTML: true,
			borderWidth: 2,
			borderColor: 'rgba(6, 191, 191, 0.48)',
			borderRadius: 8,
			backgroundColor: 'white',
			shadow: {
				color: 'rgba(0,0,0,0.1)',
				offsetX: 0,
				offsetY: 2,
				opacity: 0.15,
				width: 6,
			},
			padding: 12,
			style: {
				fontSize: '13px',
				color: 'rgba(0, 124, 124, 1)',
			},
			formatter: function () {
				const time = this.x;
				let tooltipHTML = '';

				this.points?.forEach((point) => {
					const seriesName =
						point.series.name === 'Energy' ? 'Energy' : 'Charging Time';
					const value = point.y?.toFixed(2);
					const unit = point.series.name === 'Energy' ? 'kWh' : 'mins';
					tooltipHTML += `
            <div style="margin-bottom:4px;">
              <b>${seriesName}:</b> ${value} ${unit}
            </div>
          `;
				});
				tooltipHTML += `<div style="font-weight:600;margin-bottom:8px; color:#7C8D8F">${time}</div>`;

				return tooltipHTML;
			},
		},

		series: [
			{
				type: 'areaspline',
				name: 'Energy',
				data: finalEnergyData,
				yAxis: 0,
				color: '#00C3C2',
				lineWidth: 2,
				fillColor: {
					linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
					stops: [
						[0, 'rgba(255, 255, 255, 0)'],
						[1, '#00C3C2'],
					],
				},
				marker: {
					enabled: false,
				},
			},
			{
				type: 'spline',
				name: 'Charging Time',
				data: finalChargingTimeData,
				yAxis: 1,
				color: '#5e7a9b',
				lineWidth: 2,
				marker: {
					enabled: false,
				},
			},
		],
	}),[perfomanceData]);

	useLayoutEffect(() => {
		if (!chartWrapRef.current) return;

		let rafId: number | null = null;

		const observer = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;

			const newWidth = Math.floor(width);
			const newHeight = Math.floor(height);

			const prev = chartSizeRef.current;
			if (prev.width === newWidth && prev.height === newHeight) return;

			chartSizeRef.current = {
				width: newWidth,
				height: newHeight,
			};

			// Break ResizeObserver → layout loop
			if (rafId) cancelAnimationFrame(rafId);

			rafId = requestAnimationFrame(() => {
				const chart = chartRef.current?.chart;
				if (chart) {
					chart.setSize(newWidth, newHeight, false);
				}
			});
		});

		observer.observe(chartWrapRef.current);

		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			observer.disconnect();
		};
	}, []);
	return (
		<div className='charging-session-performance' ref={chartWrapRef}>
			<HighchartsReact
				highcharts={Highcharts}
				options={options}
				immutable={false}
				allowChartUpdate={true}
				updateArgs={[true, true, true]}
				ref={chartRef}
			/>
		</div>
	);
};

export default ChargingSessionAreaChart;
